/** Lo que dice el chat: el hilo, sus componentes, el dictado y la burbuja. */
export const CHAT_TEXTS = {
  thread: {
    emptyTitle: "¿Qué necesitas?",
    emptyHint:
      "Adjunta un Excel y pídeme que lo revise, o pregúntame por lo que ya esté cargado.",
    loadingHistory: "Recuperando la conversación…",
    compactEmpty:
      "Pregúntame por lo que estás viendo sin salir de esta pantalla.",
    compacted: (count: number | string) =>
      `Los primeros ${String(count)} mensajes están resumidos para que el asistente pueda seguir`,
    openError: "No se pudo abrir la conversación",
    uploadError: "No se pudo subir el archivo",
    turnError: "No se pudo completar el turno",
    understood: "Entendido",
    stillWriting: "Sigue escribiendo ↓",
    goToEnd: "Ir al final ↓",
    thinking: "Pensando",
  },
  bubble: {
    openWaiting: (waiting: number) =>
      `Abrir el chat · ${String(waiting)} esperando tu respuesta`,
    open: "Abrir el chat",
    dialog: "Chat con el asistente",
    assistant: "Asistente",
    close: "Cerrar el chat",
  },
  message: {
    user: "Tú",
    assistant: "Asistente",
  },
  confirmation: {
    actions: (many: number) =>
      many === 1 ? "1 acción" : `${String(many)} acciones`,
    failedToRun: "Falló al ejecutarse",
    approve: (many: number) =>
      many === 1 ? "Aprobar" : `Aprobar las ${String(many)}`,
    reject: (many: number) => (many === 1 ? "Rechazar" : "Rechazar todas"),
    badge: {
      waiting: "Requiere tu confirmación",
      approved: "Aprobada",
      rejected: "Rechazada",
      expired: "Caducó",
      resolved: "Resuelta",
    },
    closing: {
      approvedOne: "Aprobaste esta acción.",
      approvedMany: (many: number) => `Aprobaste las ${String(many)} acciones.`,
      rejectedOne: "Rechazaste esta acción.",
      rejectedMany: (many: number) =>
        `Rechazaste las ${String(many)} acciones.`,
      doneOne: "Se hizo.",
      doneMany: "Se hicieron todas.",
      failedOne: "Se aprobó, pero no se pudo hacer.",
      failedMany:
        "Las demás se hicieron igual: una que falla no deshace el resto. Despliega la que falló para ver por qué.",
      notDoneOne: "Rechazada: no se hizo nada.",
      notDoneMany: "Rechazadas: no se hizo ninguna.",
      expired: "Caducó sin respuesta: no se hizo nada.",
      resolvedOne: "Esta acción ya se resolvió o caducó.",
      resolvedMany: "Estas acciones ya se resolvieron o caducaron.",
    },
    failed: "Falló",
    itemDone: "hecha",
    itemFailed: "falló",
    withError: (error: string) => `: ${error}`,
  },
  results: {
    done: "Hecha",
    failed: "Falló",
    someDone: (done: number, total: number) =>
      `${String(done)} de ${String(total)} hechas`,
    withFailures: (head: string, failed: number) =>
      `${head} · ${String(failed)} ${failed === 1 ? "falló" : "fallaron"}`,
  },
  questions: {
    answered: "Respondido",
    needsAnswer: "Necesita tu respuesta",
    progress: (given: number, total: number) =>
      `${String(given)} de ${String(total)}`,
    other: "Otra",
    otherHint: "Ninguna encaja: la escribo yo.",
    yourAnswer: "Tu respuesta",
    notePlaceholder: "Algo más que quieras añadir",
    addNote: "Añadir nota",
    alreadyAnswered: "Estas preguntas ya se respondieron.",
    submit: "Responder",
    next: "Siguiente",
  },
  composer: {
    tooBig: (name: string, megabytes: number) =>
      `"${name}" pesa demasiado. El máximo son ${String(megabytes)} MB.`,
    attachTitle: "Adjuntar un Excel o CSV",
    attachLabel: "Adjuntar archivo",
    placeholder: "Escribe o dicta lo que necesitas…",
    stop: "Detener",
    send: "Enviar",
  },
  table: {
    noDataTitle: "Tabla sin datos",
    noData:
      "La herramienta no devolvió ni filas ni un identificador de dataset.",
    loading: "Cargando datos…",
    loadError: "No pude cargar la tabla",
    more: (remaining: string) => `Ver más (${remaining} restantes)`,
    result: "Resultado",
    rows: (total: string, one: boolean) => `${total} ${one ? "fila" : "filas"}`,
    shown: (loaded: string) => ` · se muestran ${loaded}`,
  },
  toolCall: {
    running: "consultando",
    error: "falló",
    done: "consultado",
    waiting: "espera tu confirmación",
    approved: "aprobada",
    executed: "hecha",
    failedToRun: "falló al ejecutarse",
    rejected: "rechazada",
    expired: "caducó",
    resolved: "confirmación resuelta",
    input: "Entrada",
    errorSection: "Error",
    whyFailed: "Por qué falló",
    result: "Resultado",
  },
  chart: {
    malformed: "Gráfico mal formado",
    invalidSpec: "La herramienta no devolvió una especificación válida.",
    loading: "Cargando gráfico…",
    loadError: "No pude cargar el gráfico",
    viewChart: "Ver gráfico",
    viewTable: "Ver tabla",
    ariaLabel: (series: string, category: string) =>
      `Gráfico de ${series} por ${category}`,
  },
  value: {
    nothing: "La herramienta no devolvió nada.",
    yes: "Sí",
    no: "No",
  },
  resource: {
    noScreen: "no tiene pantalla propia todavía",
    unknown: "esta versión de la aplicación todavía no sabe abrir esto",
  },
  dictation: {
    interimHint:
      "El texto en cursiva al final todavía no es definitivo: se está dictando.",
  },
  download: {
    noLinkTitle: "No hay enlace de descarga",
    noLink: "La herramienta generó el archivo pero no devolvió dónde está.",
    file: "archivo",
    rows: (total: string, one: boolean) => `${total} ${one ? "fila" : "filas"}`,
    expires: (when: string) => `caduca ${when}`,
    sheets: (count: number) => `${String(count)} hojas:`,
    today: "hoy",
    tomorrow: "mañana",
    inDays: (days: number) => `en ${String(days)} días`,
  },
  compact: {
    doesNotFit: "No cabe aquí",
    kindDoesNotFit: (kind: string) => `${kind} · no cabe aquí`,
    openInChat: "Ver en el chat",
  },
  registry: {
    unregistered: (render: string) => `Componente no registrado: ${render}`,
    asked: "El backend pidió dibujar ",
    unknownFrontend: ", pero el frontend no lo conoce. Falta registrarlo en ",
    end: ".",
  },
  reasoning: "Razonamiento",
  kinds: { table: "Tabla", chart: "Gráfico", document: "Documento" },
  document: {
    empty: "Ese documento está vacío.",
  },
  conversations: {
    untitled: "Conversación sin título",
    interrupted: "El turno se interrumpió antes de que terminara.",
  },
  voice: {
    unsupportedTitle: "Este navegador no puede grabar audio",
    unsupportedLabel: "Dictar por voz (no disponible en este navegador)",
    discardTitle: "Descartar el dictado",
    cancelLabel: "Cancelar dictado",
    recording: "Grabando",
    stopTitle: "Terminar y transcribir",
    stopLabel: "Terminar el dictado y transcribir",
    transcribing: "Transcribiendo…",
    requestingPermission: "Pidiendo permiso para usar el micrófono…",
    cancel: "Cancelar",
    dictate: "Dictar por voz",
    understood: "Entendido",
    interimNotice: "Lo que aparece en cursiva todavía se está dictando.",
    tooShort: "Ha sido demasiado corto. Habla un momento más antes de parar.",
    reviewTranscript:
      "No se pudo revisar la transcripción: revisa el texto antes de enviarlo.",
    recordingCut: "Se ha cortado la grabación. Inténtalo otra vez.",
    degraded:
      "Se ha perdido la vista previa, pero se sigue grabando: al terminar se transcribirá igual.",
    micDenied:
      "No has dado permiso para usar el micrófono. Actívalo en los permisos del navegador y vuelve a intentarlo.",
    micNotFound: "No se ha encontrado ningún micrófono conectado.",
    micBusy: "El micrófono lo está usando otra aplicación.",
    micFailed: "No se pudo abrir el micrófono. Inténtalo otra vez.",
    micPlug: "No se pudo enchufar el micrófono.",
    cancelled: "Dictado cancelado.",
    network: "No se pudo contactar con el servidor. Comprueba la conexión.",
    nothingHeard:
      "No se ha entendido nada. Prueba a hablar más cerca del micrófono.",
    tooHeavy: "El audio pesa demasiado. Graba un dictado más corto.",
    unusable: "Ese audio no se pudo usar. Inténtalo otra vez.",
    transcribeFailed: (status: number) =>
      `No se pudo transcribir el audio (error ${String(status)}).`,
    liveLost: "Se perdió la conexión del dictado en directo.",
    liveClosed: "Se cerró la conexión del dictado en directo.",
    liveUnsupported: "Este navegador no puede transcribir en directo.",
    liveOpenFailed: "No se pudo abrir el canal de dictado en directo.",
  },
} as const;
